// src/hooks/useUserStats.js - FIXED: Enhanced with better error handling and null safety
import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import enhancedUserService from '../services/enhancedUserService'; // ✅ Only enhanced service

/**
 * ✅ FIXED: Enhanced user statistics hook with comprehensive error handling
 */
export const useUserStats = () => {
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isDevelopment = import.meta.env.MODE === 'development';

  useEffect(() => {
    const fetchUserStats = async () => {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setUserStats(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        console.log('📊 Fetching user stats for:', userId);

        // ✅ FIXED: Use getCompleteUserData method
        const result = await enhancedUserService.getCompleteUserData(userId);

        if (result.success && result.data) {
          console.log('✅ User stats loaded successfully');
          setUserStats(result.data);
          setError(null);
        } else {
          // ✅ FIXED: Try legacy method as fallback
          console.log('Trying legacy getUserProgress method...');
          const legacyData = await enhancedUserService.getUserProgress(userId);

          if (legacyData) {
            // Transform legacy data to expected format
            const transformedData = {
              profile: legacyData,
              recentSessions: [],
              recentExams: [],
              recentVocabulary: [],
              recentAchievements: []
            };
            setUserStats(transformedData);
            setError(null);
          } else {
            // ✅ FIXED: Create default data structure
            const defaultData = {
              profile: enhancedUserService.getDefaultProgress(userId),
              recentSessions: [],
              recentExams: [],
              recentVocabulary: [],
              recentAchievements: []
            };
            setUserStats(defaultData);
            setError(null);
          }
        }
      } catch (err) {
        console.error('❌ Error fetching user stats:', err);

        if (isDevelopment) {
          // ✅ FIXED: Provide meaningful fallback in development
          console.warn('🔧 Development mode: Using fallback data for user stats');
          const fallbackData = {
            profile: {
              userId: userId,
              totalAnswered: 0,
              correctAnswers: 0,
              studyStreak: 0,
              stats: {
                currentStreak: 0,
                totalStudyTimeMinutes: 0,
                totalCorrectAnswers: 0,
                totalQuestionsAttempted: 0,
                lessonsCompleted: 0,
                examsCompleted: 0
              },
              achievements: { unlocked: [], totalPoints: 0 }
            },
            recentSessions: [],
            recentExams: [],
            recentVocabulary: [],
            recentAchievements: []
          };
          setUserStats(fallbackData);
          setError(null);
        } else {
          setError(err.message);
          // Still provide empty structure to prevent crashes
          setUserStats({
            profile: null,
            recentSessions: [],
            recentExams: [],
            recentVocabulary: [],
            recentAchievements: []
          });
        }
      }
      setLoading(false);
    };

    // ✅ FIXED: Auth state listener with proper cleanup
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        fetchUserStats();
      } else {
        setUserStats(null);
        setError(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [isDevelopment]);

  // ✅ FIXED: Enhanced refresh method
  const refreshStats = async () => {
    setLoading(true);
    const userId = auth.currentUser?.uid;
    if (userId) {
      try {
        const result = await enhancedUserService.getCompleteUserData(userId);

        if (result.success && result.data) {
          setUserStats(result.data);
          setError(null);
        } else {
          console.warn('Failed to refresh stats, keeping current data');
        }
      } catch (err) {
        if (!isDevelopment) {
          setError(err.message);
          console.error('Error refreshing user stats:', err);
        } else {
          console.warn('Development mode: Refresh error handled gracefully');
        }
      }
    }
    setLoading(false);
  };

  // ✅ FIXED: Enhanced update method
  const updateUserProgress = async (progressData) => {
    try {
      // Transform completedLessonId to activityId for trackStudySession
      const sessionData = {
        ...progressData,
        type: progressData.type || 'lesson',
        activityId: progressData.completedLessonId || progressData.activityId,
        title: progressData.title || `Lesson ${progressData.completedLessonId || progressData.activityId || ''}`,
        completed: true,
        duration: progressData.timeSpent || progressData.duration || 0,
        durationUnit: 'minutes'
      };
      
      const result = await enhancedUserService.trackStudySession(sessionData);
      if (result.success) {
        // Refresh stats after successful update
        await refreshStats();
        return result;
      } else {
        if (!isDevelopment) {
          console.error('Failed to update user progress:', result.error);
        }
        return result;
      }
    } catch (error) {
      console.error('Error updating user progress:', error);
      return { success: false, error: error.message };
    }
  };

  // ✅ FIXED: Safe data extraction with comprehensive null checks
  return {
    userStats,
    loading,
    error,
    refreshStats,
    updateUserProgress,

    // ✅ FIXED: Safe accessors with fallbacks
    profile: userStats?.profile || null,
    recentSessions: userStats?.recentSessions || [],
    recentExams: userStats?.recentExams || [],
    recentVocabulary: userStats?.recentVocabulary || [],
    recentAchievements: userStats?.recentAchievements || [],

    // ✅ FIXED: Status indicators
    isDevelopment,
    hasData: Boolean(userStats?.profile),
    isAuthenticated: Boolean(auth.currentUser?.uid)
  };
};

/**
 * ✅ FIXED: Enhanced user progress hook for backward compatibility
 */
export const useUserProgress = () => {
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserProgress = async () => {
      const userId = auth.currentUser?.uid;
      if (userId) {
        try {
          // ✅ FIXED: Use getCompleteUserData and extract progress
          const result = await enhancedUserService.getCompleteUserData(userId);

          if (result.success && result.data?.profile) {
            const userData = result.data.profile;
            // Transform to legacy userProgress format
            const progressData = {
              userId: userId,
              totalAnswered: userData.totalAnswered || userData.stats?.totalQuestionsAttempted || 0,
              correctAnswers: userData.correctAnswers || userData.stats?.totalCorrectAnswers || 0,
              sectionScores: userData.sectionScores || {},
              completedWeeks: userData.completedWeeks || [],
              studyStreak: userData.studyStreak || userData.stats?.currentStreak || 0,
              lastStudyDate: userData.lastStudyDate || null,
              overallProgress: userData.overallProgress || 0,
              sectionProgress: userData.sectionProgress || {}
            };
            setUserProgress(progressData);
          } else {
            // ✅ FIXED: Fallback to legacy method
            const legacyProgress = await enhancedUserService.getUserProgress(userId);
            setUserProgress(legacyProgress);
          }
        } catch (err) {
          console.error('Error fetching user progress:', err);
          // ✅ FIXED: Provide fallback in development
          if (import.meta.env.MODE === 'development') {
            setUserProgress({
              userId: userId,
              totalAnswered: 0,
              correctAnswers: 0,
              sectionScores: {},
              completedWeeks: [],
              studyStreak: 0,
              lastStudyDate: null,
              overallProgress: 0,
              sectionProgress: {}
            });
          }
        }
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        fetchUserProgress();
      } else {
        setUserProgress(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return { userProgress, loading };
};

/**
 * ✅ FIXED: Enhanced achievements hook
 */
export const useAchievements = () => {
  const { userStats, loading } = useUserStats();

  // ✅ FIXED: Safe data extraction with fallbacks
  const achievements = userStats?.profile?.achievements || {
    unlocked: [],
    totalPoints: 0,
    inProgress: {}
  };
  const recentAchievements = userStats?.recentAchievements || [];

  return {
    achievements,
    recentAchievements,
    unlockedCount: achievements.unlocked?.length || 0,
    totalPoints: achievements.totalPoints || 0,
    inProgress: achievements.inProgress || {},
    loading,
    hasAchievements: achievements.unlocked?.length > 0
  };
};